
import { Edges, Image, Line, shaderMaterial, Text, useScroll, useTexture, useIntersect } from "@react-three/drei"
import { extend, useFrame } from "@react-three/fiber"
import gsap from "gsap"
import { useEffect, useRef, useState } from "react"
import textVertexShader from '../../shaders/text/vertex.glsl'
import textFragmentShader from '../../shaders/text/fragment.glsl'
import { useControls } from "leva"
import { DoubleSide } from "three"

import { mainStore } from "../../store"
import { __ } from "../../utils"

let titleVisibleSingleton = false

export default function HeadChapterFour({ props }) {

    const [isTitleVisible, setIsTitleVisible] = useState(false)

    // Texture loader
    const bgTexture = useTexture(import.meta.env.BASE_URL + 'static/text-texture.webp')

    // Utils
    const data = useScroll()

    const colors = mainStore.useStore(s => s.colors)
    const fonts = mainStore.useStore(s => s.fonts)
    const scrollMap = mainStore.useStore(s => s.scrollMap)



    let isBgReveal = false

    // Controls
    const { uTextFrequency, uTextElevation, uTextSpeed, uDisplaceX, uDisplaceY, uDisplaceSpeed, uDisplaceStep, uDisplaceClamp } = useControls('headTextTwo', {
        uDisplaceX: {
            value: 3.42,
            step: 0.001,
            min: 0,
            max: 20
        },
        uDisplaceY: {
            value: 3.58,
            step: 0.001,
            min: 0,
            max: 20
        },
        uDisplaceSpeed: {
            value: 0.1,
            step: 0.001,
            min: 0,
            max: 20
        },
        uDisplaceStep: {
            value: 0.7,
            step: 0.001,
            min: -5,
            max: 20
        },
        uDisplaceClamp: {
            value: -5,
            step: 0.001,
            min: -5,
            max: 20
        },
        uTextFrequency: {
            value: 0.59,
            step: 0.001,
            min: 0,
            max: 20
        },
        uTextElevation: {
            value: 0,
            step: 0.001,
            min: 0,
            max: 20
        },
        uTextSpeed: {
            value: 0.58,
            step: 0.001,
            min: 0,
            max: 20
        },
    })

    // Refs
    const titleRef = useRef()
    const subTitleRef = useRef()
    const leftLineRef = useRef()
    const rightLineRef = useRef()
    const bgRef = useRef()
    const paperRef = useRef()
    const meshRef = useIntersect((visible) => { setIsTitleVisible(visible), visible ? mainStore.hide() : mainStore.reveal() })

    // Intersect
    const chevronRef = useRef()

    // Shader
    const ImageShaderMaterial = shaderMaterial(
        { uTime: 0, uTextFrequency, uTextElevation, uTextSpeed, uDisplaceX, uDisplaceY, uDisplaceSpeed, uDisplaceStep, uDisplaceClamp },
        textVertexShader,
        textFragmentShader
    )

    useEffect(() => {

        if (isTitleVisible && !titleVisibleSingleton) {
            console.log('trigger title visible')


            gsap.fromTo(bgRef.current.material,
                {
                    uDisplaceStep: 1,

                },
                {
                    uDisplaceStep: - 0.7,
                    duration: 3,
                }
            )

            gsap.fromTo(bgRef.current.material,
                {
                    uDisplaceClamp: -5,
                    uDisplaceStep: - 0.7
                },
                {
                    uDisplaceClamp: 0.7,
                    uDisplaceStep: 0.1,
                    duration: 3,
                    delay: 3.5
                }
            )

            gsap.fromTo(titleRef.current,
                {
                    fillOpacity: 0,
                },
                {
                    fillOpacity: 1,
                    duration: 6
                }
            )

            gsap.fromTo(titleRef.current.position,
                {
                    y: -1,
                    z: 5
                },
                {
                    z: 0,
                    y: 0,
                    duration: 2,
                    ease: 'inout'
                }
            )

            gsap.fromTo(subTitleRef.current,
                {
                    fillOpacity: 0
                },
                {
                    fillOpacity: 1,
                    duration: 1.5,
                    delay: 2
                }

            )

            gsap.fromTo(leftLineRef.current.position, {
                x: -1,
            },
                {
                    x: 0,
                    duration: .7,
                    delay: 1,
                    ease: 'out'
                })


            gsap.fromTo(rightLineRef.current.position,
                {
                    x: 1,
                },
                {
                    x: 0,
                    duration: .7,
                    delay: 1,
                    ease: 'out'
                })

            titleVisibleSingleton = true
        }

    }, [isTitleVisible])


    useFrame((state, delta) => {

        // title 
        titleRef.current.position.z = 0 + data.range(scrollMap.three + 0.1 * scrollMap.five, 0.3 * scrollMap.five) * 3
        subTitleRef.current.position.z = 0 + data.range(scrollMap.three + 0.1 * scrollMap.five, 0.3 * scrollMap.five) * 3


        // Square
        meshRef.current.position.z = -3 + data.range(scrollMap.three + 0.1 * scrollMap.five, 0.2 * scrollMap.five) * 5
        meshRef.current.rotation.z += delta * 0.2
        meshRef.current.rotation.y += delta * 0.2

        // Text background
        bgRef.current.material.uTime += delta
        paperRef.current.material.opacity = .35 - data.range(scrollMap.three + 0.1, scrollMap.three + 0.2 * scrollMap.five) * 2

        bgRef.current.fillOpacity = 0.15 - data.range(scrollMap.three + 0.1 * scrollMap.five, scrollMap.three + 0.2 * scrollMap.five) * 0.6


    })

    extend({ ImageShaderMaterial })

    return <>
        <group position={[0, -68.3, 1]}>
            <Text
                ref={titleRef}
                strokeWidth={0.001}
                fillOpacity={0}
                maxWidth={1}
                lineHeight={0.8}
                fontSize={0.29}
                position={[0, 0, 1]}
                font={fonts.cm.title}
                color={colors.title}
            >
                {__('RAYONNEMENT', 'OUTREACH')}
            </Text>
            <Text
                ref={subTitleRef}
                maxWidth={1}
                lineHeight={0.8}
                fontSize={0.059}
                position={[0, -0.16, 1]}
                font={fonts.ms.text}
                color={colors.title}
            >
                2010 - 2020
            </Text>
            <group position={[0, 0, 3]}>
                <Line
                    ref={leftLineRef}
                    points={[[-2, 0, -1], [-0.6, 0, -1]]}
                    color={colors.title}
                    lineWidth={1}
                    position={[-1, 0, 0]}
                />
                <Line
                    ref={rightLineRef}
                    points={[[0.6, 0, -1], [2, 0, -1]]}
                    color={colors.title}
                    lineWidth={1}
                />
            </group>
            <mesh ref={meshRef} scale={1.5} position-z={-3}>
                <planeGeometry />
                <Edges
                    linewidth={0.7}
                    scale={1.1}
                    threshold={15} // Display edges only when the angle between two faces exceeds this value (default=15 degrees)
                    color="black"
                    opacity={.15}
                />
                <meshBasicMaterial side={DoubleSide} visible={true} color={colors.clear} />
            </mesh>


            <Image
                ref={paperRef}
                url={import.meta.env.BASE_URL + "static/paper-texture.webp"}
                transparent
                scale={20}
                opacity={0.15}
                zoom={0.6}
                segments={4}
                position={[0, 0, -8]}

                toneMapped={false}
                color={colors.clear}
            >
                <planeGeometry />
            </Image>


            {
                __(


                    <Text
                        ref={bgRef}
                        position={[0, -0.5, -5]}
                        maxWidth={8}
                        color={'black'}
                        scale={1}
                        font={fonts.cd.title}
                        fontSize={0.23}
                        textAlign="center"
                        fillOpacity={0.15}
                        rotation={[- Math.PI * 0.0, 0, Math.PI * 0.00]}
                        lineHeight={3.6}
                    >

                        Crèche inter-entreprises
                        Pour faciliter l’équilibre vie privée / professionnelle des jeunes parents parmi les collaborateurs, l'entreprise initie la création d’une crèche inter-entreprise, située aux portes de Silab.
                        Partenariat avec le CABCL Association
                        Le partenariat de Silab avec l’équipe de rugby locale débute par un soutien financier de son centre de formation.


                        <imageShaderMaterial attach="material" uDisplaceClamp={uDisplaceClamp} uDisplaceStep={uDisplaceStep} uDisplaceSpeed={uDisplaceSpeed} uDisplaceX={uDisplaceX} uDisplaceY={uDisplaceY} uTextElevation={uTextElevation} uTextFrequency={uTextFrequency} uTextSpeed={uTextSpeed} />
                    </Text>
                    ,
                    <Text
                        ref={bgRef}
                        position={[0, -0.5, -5]}
                        maxWidth={8}
                        color={'black'}
                        scale={1}
                        font={fonts.cd.title}
                        fontSize={0.23}
                        textAlign="center"
                        fillOpacity={0.15}
                        rotation={[- Math.PI * 0.0, 0, Math.PI * 0.00]}
                        lineHeight={3.6}
                    >

                        Inter-company nursery
                        To facilitate the work-life balance of young parents among its employees, the company initiates the creation of an inter-company nursery, located on the outskirts of Silab.
                        Partnership with the CABCL Association
                        Silab's partnership with the local rugby team begins with financial support for its training center.


                        <imageShaderMaterial attach="material" uDisplaceClamp={uDisplaceClamp} uDisplaceStep={uDisplaceStep} uDisplaceSpeed={uDisplaceSpeed} uDisplaceX={uDisplaceX} uDisplaceY={uDisplaceY} uTextElevation={uTextElevation} uTextFrequency={uTextFrequency} uTextSpeed={uTextSpeed} />
                    </Text>

                )
            }



            <Image
                ref={chevronRef}
                url={import.meta.env.BASE_URL + "static/icons/down.svg"}
                scale={0.08}
                opacity={.5}
                position={[0, -.87, 2]}
                transparent
            />

        </group>
    </>

}